/* eslint-disable max-len */
import React from 'react';
import styled from 'styled-components';
import { SectionName, SectionTitle } from '../common/styles';

const Container = styled.div`
  margin: ${props => (props.theme.isMobile ? '67px 0 19px 19px' : '0 auto')};
  padding-bottom: 6px;
  max-width: 1230px;
  width: 85vw;
  display: flex;
  flex-direction: column;
  align-items: start;
`;

export const Timestamp = styled(SectionName)`
  font-size: 12px;
`;

export const Title = styled(SectionTitle)`
  margin: 6px 0 0 0;
  border-bottom: 1px dashed ${props => props.theme.primaryColor};
  font-size: ${props => (props.theme.isMobile ? 25 : 40)}px;
`;

export const Section = styled.div`
  max-width: 1180px;
  width: 82vw;
  font-size: 16px;
  margin-top: 10px;
  display: flex;
  flex-direction: row;
  align-items: start;
  line-height: 25px;
`;

export const InnerSection = styled(Section)`
  max-width: 1120px;
`;

export const Paragraph = styled.p`
  position: relative;
  max-width: 449px;
  margin-top: 24px;
  color: ${props => props.theme.bodyColor};
  font-size: 20px;
  line-height: 25px;
  text-align: left;
`;

export const Headline = styled(Paragraph)`
  max-width: 1180px;
  width: 82vw;
  font-size: 20px;
  font-weight: ${props => props.theme.fontWeightBold};
  margin-top: 33px;
`;

export const SubHeadline = styled(Paragraph)`
  max-width: 1180px;
  width: 82vw;
  font-size: 18px;
`;

const Span10 = styled.span`
  margin-right: 10px;
`;

const Underscore = styled.span`
  text-decoration: underline;
`;

export default function Content() {
  return (
    <Container>
      <Title>Livewire Partner Program - General Terms</Title>

      <Headline>1. About Livewire</Headline>
      <Section>
        <span>
          Livewire Markets Pty Ltd ABN 92 156 343 501 (
          <strong>Livewire</strong>
          {' '}
          or
          {' '}
          <strong>us</strong>
          ) connects investors with new investment ideas, by partnering with leading financial services firms to provide content for our readers.
        </span>
      </Section>
      <Section>
        <span>We give readers access to information and educational content provided by financial services professionals and companies. We do not operate under an Australian financial services licence relying on the ‘media’ exemption under section 911A(2)(eb) of the Corporations Act 2001 (Cth) in respect of any advice given.</span>
      </Section>
      <Section>
        <span>
          We provide content through our own website
          {' '}
          <a
            href="https://www.livewiremarkets.com"
          >
            www.livewiremarkets.com
          </a>
          {' '}
          (
          <strong>Website</strong>
          ), through our distribution partners such as Australian Financial Review, IRESS, Google News and Bloomberg, through direct email and through other campaigns and media channels, YouTube, LinkedIn and other forms of social media (collectively called
          {' '}
          <strong>Channels</strong>
          {' '}
          in these terms).  (In these terms,
          {' '}
          <strong>Content</strong>
          {' '}
          means any data, text, posts, commentary, images, audio or video material and other content that is uploaded, posted or otherwise transmitted through our Channels.)
        </span>
      </Section>
      <Section>
        <span>We make it clear to our readers that any advice on our Channels is general in nature and does not take into consideration their objectives financial situation or needs.</span>
      </Section>
      <Section>
        <span>
          Our Terms of Use for our Website users (User Terms) are available at our Website (currently; at
          {' '}
          <a
            href="https://www.livewiremarkets.com/pages/terms_and_conditions"
          >
            https://www.livewiremarkets.com/pages/terms_and_conditions
          </a>
          ). These User Terms include general disclaimers. If they wish to Partners can add their own disclaimers to Content they provide.
        </span>
      </Section>
      <Section>
        <span>To contact us please contact your partnerships manager or email us at team@livewiremarkets.com.</span>
      </Section>
      <Section>
        <strong>Please read these terms carefully before ordering any services from us.</strong>
      </Section>

      <Headline>2. Our contract with you</Headline>
      <Section>
        <span>
          These
          <strong>General Terms</strong>
          {' '}
          apply to all products and services we provide to you. Details of our products and services fees subscription duration and any other specific terms will be set out in an
          {' '}
          <strong>Order Form</strong>
          {' '}
          issued by us to the partner company or organisation identified on the Order Form (referred to in these terms as
          {' '}
          <strong>Partner</strong>
          {' '}
          or
          {' '}
          <strong>you</strong>
          ).
        </span>
      </Section>
      <Section>
        <span>Partner accepts the Order Form by: signing it (including electronically); confirming in writing (including email) that it accepts it; asking us in writing (including email) to provide the services we have offered in the Order Form; or issuing a Purchase Order for the services we have offered in the Order Form.</span>
      </Section>
      <Section>
        <span>
          Each time the Partner accepts an Order Form it creates a separate binding agreement (
          <strong>Partner Agreement</strong>
          ) comprising that Order Form and these General Terms. If there is a conflict between these documents the Order Form prevails.
        </span>
      </Section>

      <Headline>3. Livewire Services</Headline>

      <Section>
        <span>
          The products and services we offer to our Partners include:
          <div>
            <InnerSection>
              <Span10>(a)</Span10>
              <div>
                <strong>Package Services</strong>
                {' '}
                – premium access to the Website and other Channels Services and
                distribution to our readers for a subscription fee (based on tiers) for a minimum term.
              </div>
            </InnerSection>
            <InnerSection>
              <Span10>(b)</Span10>
              <div>
                <strong>Sponsored Services and Advertising</strong>
                {' '}
                – ‘one off’ items or packages that include services
                such as: Content consulting creation and distribution event sponsorships display
                advertising or email advertising.
              </div>
            </InnerSection>
            <InnerSection>
              <Span10>(c)</Span10>
              <div>
                <strong>Contributor Profiles & Fund Profiles</strong>
                {' '}
                – Contributor Profiles give our readers more information about the people who contribute Content such as their biography, career history and education. Fund Profiles give readers more information about specific financial products such as managed funds, ETFs and LICs. The information provided includes qualitative and quantitative information (such as performance data and fees).  Some of the data is provided by Morningstar and the remainder directly from the product provider.
              </div>
            </InnerSection>
          </div>
        </span>
      </Section>
      <Section>
        <span>
          In these General Terms we refer to any products and services we offer to provide in an Order Form as our
          {' '}
          <strong>Services</strong>
          .  We agree to provide the Services to you on the terms of this Partner Agreement.
        </span>
      </Section>
      <Section>
        <span>We may modify the description or scope of our Package Services from time to time to reflect changes to our Package Service tiers or descriptions by giving you reasonable prior notice.  If you choose not to accept the changes, you may cancel your subscription by giving one month’s notice before the next invoice period.  If you continue to pay the Fees after receiving notice of the changes, you are deemed to have accepted the changes.</span>
      </Section>
      <Headline>4. Providing Content through our Channels</Headline>
      <SubHeadline><strong>4.1 Users</strong></SubHeadline>
      <Section>
        <span>
          We provide access to the Channels to our
          {' '}
          <strong>Users</strong>
          , who join for free and get access to Content.  &quot;
          <strong>Contributors</strong>
          &quot; are Users who have permission to create Content.
        </span>
      </Section>
      <Section>
        <span>As part of our Package Services, we will give you logon permissions for an agreed number of your staff to be Contributors, authorised to create and post Partner Content to the Channels. The number of Contributors will be agreed with you and will take into account a particular Contributor’s ability to generate content, rather than solely based on the total number of Contributors.</span>
      </Section>
      <Section>
        <span>You may change which of your staff are Contributors from time to time, by giving us a written request.  If a Contributor leaves your employment you must promptly notify us and, if you ask us to, we will remove them as a Contributor from your account.</span>
      </Section>
      <Section>
        <span>You acknowledge that each person you nominate as a Contributor must: (a) register to access the Website by providing all information, including a valid email address, that we ask for; and (b) use the Website in accordance with our User Terms.</span>
      </Section>
      <Section>
        <span>You are responsible for any use of the Website and Channels by your Contributors (including for ensuring they comply with the User Terms).</span>
      </Section>
      <Section>
        <span>If we reasonably believe one of your Contributors is in breach of the User Terms, Relevant Laws or this Partner Agreement, we may suspend or terminate their access to the Channels.  We will tell you if we do this, including our reasons.  You acknowledge that in serious cases we may not be able to notify you before acting.</span>
      </Section>

      <SubHeadline><strong>4.2 Restrictions on use</strong></SubHeadline>
      <Section style={{ flexDirection: 'column' }}>
        <span>The Partner and its Contributors must not:</span>
        <ul style={{ listStyleType: 'lower-alpha' }}>
          <li>
            attempt to modify, copy, adapt or reproduce, decompile, disassemble, reverse engineer or otherwise derive the source code for the software we provide to Partner or Users via the Website (
            <strong>Software</strong>
            );
          </li>
          <li>
            distribute, encumber, sell, rent, lease, sub-license or otherwise transfer, publish or disclose our Software to any third party (or provide the Website passwords or other log-in information), except to Users or as otherwise permitted under this agreement;
          </li>
          <li>
            use our Software or other materials (including features on the Website) to build a product or service which competes with us, or to copy our ideas, features, functions or graphics, or share our Software or other materials with any competitors of Livewire;
          </li>
          <li>
            use, or misuse, the Software in any way which may impair the functionality of the Software, the Website or the ability of other users to use the Software or the Website;
          </li>
          <li>
            transmit or input into the Software or the Website any files or Content that may damage any other person&apos;s computing devices or software.
          </li>
        </ul>
      </Section>

      <SubHeadline><strong>4.3 Availability</strong></SubHeadline>
      <Section>
        <span>We will use all commercially reasonable efforts to make the Channels available to you and your Contributors however you acknowledge and agree that: (a) access to the Channels may be prevented by issues outside of our control; and (b) the Channels will not operate on a continuous basis and may be unavailable from time to time (including for maintenance purposes).</span>
      </Section>

      <Headline>4.4 Partner Content</Headline>
      <Section>
        <span>
          <strong>Partner Content</strong>
          {' '}
          means Content that is: (i) uploaded, posted or otherwise transmitted through the Channels by or on behalf of Partner or its Contributors; or (ii) provided by you (or developed by us) as part of our ‘Fund Profile’ about you (including details about your fund/s and related Product Disclosure Statements) or as an additional service including as a ‘Sponsored Wire’ (or other branded promotion).
        </span>
      </Section>
      <Section>
        <span>As part of the Package Services, your Contributors may create and post your Partner Content to the Channels.  We may provide suggestions and input to Partner Content but you remain responsible for your Partner Content. This includes disclosing ownership of any securities that you may hold when discussing them as part of your Partner Content.</span>
      </Section>
      <Section>
        <span>You agree that you and your Contributors must not upload, post, or otherwise transmit Content that:</span>
      </Section>
      <Section>
        <ul style={{ listStyleType: 'lower-alpha' }}>
          <li>is materially inaccurate, misleading or incorrect;</li>
          <li>
            is harmful, obscene, pornographic, defamatory, racist, sexist, threatening, violent, offensive, abusive, vulgar, profane, indecent, unlawful, harassing, or otherwise objectionable to Livewire or other Users;
          </li>
          <li>exploits another person in any manner;</li>
          <li>includes unauthorised disclosure of personal information;</li>
          <li>infringes anyone&apos;s Intellectual Property Rights; or</li>
          <li>breaches any Relevant Laws.</li>
        </ul>
      </Section>
      <Section>
        <span>
          (
          <strong>Relevant Laws</strong>
          {' '}
          means laws, regulations, binding undertakings or orders, or the requirements of any licensing regime which are relevant to Partner’s business, the Services or the creation and publication of financial services information, including: the Corporations Act 2001, the Australian Securities and Investments Commission Act 2001, ASIC Market Integrity Rules or other ASIC regulatory guides or instruments (including Regulatory Guide 234 Advertising financial products and services), Australian Financial Services licence terms, ASX Listing Rules or Operating Rules.)
        </span>
      </Section>
      <Section>
        <span>
          As part of the Services, we may also publish your Partner Content and advertising materials as part of our ‘Fund Profile’ information, or as a ‘Sponsored Wire’ (or other branded product), in which case you agree to also comply with the restrictions in
          {' '}
          <strong>Schedule 1</strong>
          .
        </span>
      </Section>
      <Section>
        <span>Where we publish Partner Content as part of our Fund Profile information (such as your fund information, Product Disclosure Statements, fees, risks and performance data) you agree to ensure such Content is accurate and is kept up to date (for example, external links to your publications or website are kept current).</span>
      </Section>
      <Section>
        <span>We will comply with your reasonable instructions in displaying your Partner Content, and your branding, logo and other information about you, which may include the display of your own legal disclaimer to be visible to all Users accessing your Partner Content.</span>
      </Section>
      <Section>
        <span>We will promptly amend or remove from the Channels your Partner Content or your branding, logo or other information about you, where you ask us to do this in writing.</span>
      </Section>
      <Section>
        <span>We may edit or remove Partner Content that we believe breaches these terms or any Relevant Laws.</span>
      </Section>
      <Section>
        <span>We retain editorial control over the Channels and we may alter or remove any Content that we believe breaches these General Terms, or where we archive historical content.</span>
      </Section>

      <Headline>4.5 Livewire Content</Headline>
      <Section>
        <span>Livewire Content is anything that is created by Livewire and uploaded or posted to the Channels by us. This includes any content created by Livewire that incorporates contributions from Partners or Contributors.  Partner Content that we post on your behalf is not Livewire Content.</span>
      </Section>

      <Headline>5. Fees</Headline>
      <Section>
        <span>You agree to pay us the fees specified on an Order Form (Fees) on the payment terms as also specified on the Order Form.</span>
      </Section>

      <Headline>6. Confidentiality</Headline>
      <Section>
        <span>Each party acknowledges that it may receive confidential information of the other party when performing this agreement.  Each party agrees to keep the other party’s confidential information confidential; only use it for the purposes of this agreement; and only disclose it to its own staff in order to perform this agreement, or in order to comply with a law or regulation, to obtain legal or other professional advice or as part of legal proceedings under this agreement.</span>
      </Section>
      <Section>
        <span>These obligations no longer apply to information which has become publicly known, or was already known to the recipient, or is independently developed by the recipient, or was acquired from a third party without breach of confidence.</span>
      </Section>
      <Section>
        <span>Confidential information under this agreement includes our Software and the contents of our Website, our User lists and details, our media kit, writing guides and other ‘how to’ guides, and each party’s business, financial, technical, and personal information.</span>
      </Section>

      <Headline>7. Intellectual Property</Headline>
      <Section>
        <span>
          <strong>Intellectual Property Rights</strong>
          {' '}
          means all intellectual property rights (including all registered and unregistered copyright, designs, trade marks and patents) of any nature in any technology, trade secrets, domain names, software, program, inventions, designs, works and subject matter or any rights to registration of such rights, whether created before, during or after the Term.
        </span>
      </Section>
      <SubHeadline><strong>7.1 Livewire Materials</strong></SubHeadline>
      <Section>
        <span>
          <strong>Livewire Materials</strong>
          {' '}
          means materials created by or on behalf of Livewire and provided to Partner in providing Services including our Software Website documentation Livewire Content and other materials or reports provided as part of the Services. You acknowledge that: you have rights to access and use the Livewire Materials on the terms of this agreement but Livewire and/or its licensors retains ownership of all Intellectual Property Rights in the Livewire Materials and no development or customization to your specifications or in accordance with your feedback or suggestions entails ownership by you of any Intellectual Property Rights in any development.
        </span>
      </Section>
      <SubHeadline><strong>7.2 Partner Content</strong></SubHeadline>
      <Section>
        <span>We acknowledge that you retain ownership of all Intellectual Property Rights in Partner Content.</span>
      </Section>
      <Section>
        <span>You grant us a non-exclusive royalty-free worldwide and transferable right and licence (including the right to sublicense) to use copy transmit store and modify the Partner Content and publish the Partner Content to the public via any media channel. This licence continues indefinitely unless you delete the Partner Content as allowed by our policies from time to time.</span>
      </Section>
      <Section>
        <span>You are responsible for ensuring that any of your Contributors waives any moral rights in the Partner Content (or gives consent to how we may use the Partner Content).</span>
      </Section>
      <Section>
        <span>We may use your branding logo trade mark and other Intellectual Property only for the purpose of providing the Services to you including to help Users on the Website/s to navigate to your company profile or to promote your use of the Channels or your Partner Content. We will use your logos and trade marks according to written usage or formatting guidelines you give us.</span>
      </Section>
      <Headline>8. Liability</Headline>
      <SubHeadline><strong>8.1 Partner</strong></SubHeadline>
      <Section>
        <span>In their use of the Services, the Partner must comply (and take all reasonable steps to ensure that its Contributors comply) with Relevant Laws, including not engaging in activities which may constitute: market manipulation, false trading, market rigging, fictitious or illegal transactions, false or misleading statements, fraudulent, misleading or deceptive conduct, or insider trading.</span>
      </Section>
      <Section style={{ fontWeight: 'bold' }}>
        <span>During and after the Term, the Partner agrees to indemnify Livewire (and its directors, employees and agents) against any claims, damages, losses, liabilities, settlements and expenses (including reasonable legal costs) incurred by Livewire as a result of any claim or action against Livewire by a third party (including a User) which results from (i) any claims based on Partner Content (including Intellectual Property or privacy infringement, or misleading conduct); or (ii) any breach by Partner or its Contributors of Relevant Laws.</span>
      </Section>
      <SubHeadline><strong>8.2 Livewire Warranties</strong></SubHeadline>
      <Section>
        <span>Livewire warrants to the Partner that the Services: (a) will be provided with due care and skill; (b) will be fit for the purpose for which they are provided; and (c) will be provided within a reasonable time frame.</span>
      </Section>
      <Section>
        <span>Partner acknowledges and agrees that: (a) Livewire is not responsible for the activities of any User; (b) Livewire makes no representation or warranty: (i) that access to the Services or Channels will be secure, uninterrupted, virus-free and/or error-free; (ii) about the accuracy, reliability or completeness of information on the Channels; or (iii) that Partner will receive a minimum number of readers, engagements, inquiries or other commercial benefits from using the Services or posting Partner Content.</span>
      </Section>

      <SubHeadline><strong>8.3 Limitation of Liability</strong></SubHeadline>
      <Section style={{ fontWeight: 'bold' }}>
        <span>To the fullest extent permitted by law and regardless of the form of action, whether in contract, tort including negligence, pursuant to statute, or otherwise:</span>
      </Section>
      <Section>
        <ul style={{
          listStyleType: 'lower-alpha',
          fontWeight: 'bold',
        }}
        >
          <li>
            neither party (and any of its directors, employees, agents or related companies) shall be liable for any down-time, lost profit or product, loss of data, increased cost of production, loss of client goodwill or good standing or delay in operation, lost opportunity or any other special, punitive, economic, indirect or consequential loss or damage (except where such losses are incurred by Livewire as a result of a third party claim covered by the indemnity in clause 8.1); and
          </li>
          <li>
            the liability of Livewire (and any of its directors, employees, agents or related companies) relating to the performance of this agreement or the supply of the Software, Channels and Services is limited in aggregate to the Fees paid by the Partner to Livewire in the 12 months prior to the act or omission which gave rise to the liability.
          </li>
        </ul>
      </Section>
      <Section>
        <span>Under the Competition and Consumer Act 2010 (Cth) you may have certain rights and remedies available to you for breach of a consumer guarantee. For a breach of a consumer guarantee held to be a major failure your rights include terminating the contract and receiving a refund for the parts of the services that have not been provided. For breach of a consumer guarantee held to be a minor failure your rights include Livewire rectifying the problem free of charge and within a reasonable time period.</span>
      </Section>

      <SubHeadline><strong>8.4 General</strong></SubHeadline>
      <Section>
        <span>Each party’s liability shall be reduced proportionately to the extent that the other party’s acts or omissions causes or contributes to the loss or damage for which the first party is liable.</span>
      </Section>
      <Section>
        <span>Each party will not be liable for, or be considered to be in breach of or default under this Agreement on account of any delay or failure to perform as required by this Agreement (except an obligation to pay the Fees) as a result of any cause or condition beyond that party’s reasonable control.</span>
      </Section>

      <Headline>9. Term & Termination</Headline>
      <Section>
        <span>
          This Partner Agreement begins on the
          <strong>Start Date</strong>
          {' '}
          set out the Order Form and continues for so long as we are providing any Services to you (
          <strong>Term</strong>
          ).
        </span>
      </Section>
      <Section>
        <span>Either party may terminate this agreement if the other party: (a) breaches this agreement and does not remedy the breach within one month of written notice; (b) breaches any term of this agreement which cannot be remedied (including the restrictions on use in clause 2 and a breach of confidentiality or Intellectual Property Rights); or (c) becomes insolvent or bankrupt or has an administrator, receiver or liquidator appointed.</span>
      </Section>
      <Section>
        <span>
          For Package Services: (a) the Term continues for the
          <strong>Minimum Term</strong>
          {' '}
          set out on the Order Form and then
          <strong>automatically renews</strong>
          {' '}
          each quarter until terminated under this clause; (b) after the Minimum Term: (i) you may terminate this agreement at any time for convenience by giving us written notice (but shall not be entitled to any refund of Fees already paid); and (ii) we may terminate this agreement for any reason outside our control which compromises our ability to provide you with the access to the Channels or the Services by giving at least three months’ written notice.
        </span>
      </Section>
      <Section>
        <span>Upon any termination: (a) the rights of Partner and Contributors to access and use the Channels and post Content in those capacities terminate (unless we allow the Partner to continue as a Contributor only); and (b) all sections of this agreement which by their nature should survive termination will survive including accrued rights to payment confidentiality obligations warranty disclaimers limitations of liability and indemnities.</span>
      </Section>

      <Headline>10. General</Headline>
      <Section>
        <span>
          <Underscore>Entire Agreement.</Underscore>
          {' '}
          This Partner Agreement contains everything the parties have agreed in relation to the matters it deals with. You acknowledge that you have not relied on any warranty or representation by us except for the warranties or representations specifically stated in these General Terms or in an Order Form. Despite any acknowledgment by us of a purchase order or other document submitted by you, any term of such purchase order or other document which is inconsistent with or which adds to the terms of this Partner Agreement is null and void.
        </span>
      </Section>
      <Section>
        <span>
          <Underscore>Interpretation.</Underscore>
          {' '}
          Words like including, for example, such as or similar expressions are to be interpreted as meaning including, without limitation. If a clause or part of a clause can be read in a way that makes it illegal, unenforceable or invalid, but can also be read in a way that makes it legal, enforceable and valid, it must be read in the latter way. If any clause or part of a clause is illegal, unenforceable or invalid, that clause or part is to be treated as removed from this document, but the rest of this document is not affected.
        </span>
      </Section>
      <Section>
        <span>
          <Underscore>Variation & Waiver.</Underscore>
          {' '}
          A variation of this agreement is only effective if it is in writing and signed by both of us. The fact that a party fails to do, or delays in doing, something the party is entitled to do under this agreement does not amount to a waiver of any obligation of, or breach of obligation by, another party. A waiver by a party is only effective if it is in writing.
        </span>
      </Section>
      <Section>
        <span>
          <Underscore>Assignment.</Underscore>
          {' '}
          Either of us may assign this agreement as part of any corporate reorganisation or sale of business, provided: (i) the assignee agrees in writing to assume all the obligations of the assigning party under this agreement, and (ii) the assigning party and the assignee give the other party written notice of the assignment.
        </span>
      </Section>
      <Section>
        <span>
          <Underscore>Independent Contractors – not legal ‘partners’.</Underscore>
          {' '}
          The term ‘Partner’ is used in this agreement to describe a close commercial relationship however the parties expressly agree that they are independent contractors for purposes of this agreement and not legal partners or agents. No party has authority to bind the other party.
        </span>
      </Section>
      <Section>
        <span>
          <Underscore>Governing law.</Underscore>
          {' '}
          This agreement is governed by the laws of New South Wales, Australia. Each of the parties submits to the non-exclusive jurisdiction of courts with jurisdiction there.
        </span>
      </Section>

      <Headline>Schedule 1 – Advertising Restrictions</Headline>
      <Section>
        <span>
          In this Schedule,
          {' '}
          <strong>Advertisement</strong>
          {' '}
          means Partner Content and advertising materials to be published as part of our Fund Profile information, or to be identified as a ‘Sponsored Wire’ (or other branded offering) including product or fund information, Product Disclosure Statements, fund fees and performance data, e-mail address, URL address, graphics and any other advertisement content for publishing and/or distribution you supply to us, or which we develop for you as part of our Services and which you approve for publication.
          <br />
          You warrant to Livewire that:
          <div>
            <InnerSection>
              <Span10>(a)</Span10>
              the Advertisement for any financial products and credit products give a balanced message about the returns, features, benefits and risks associated with the product;
            </InnerSection>
            <InnerSection>
              <Span10>(b)</Span10>
              warnings, disclaimers and qualifications are not inconsistent with other content in the Advertisement;
            </InnerSection>
            <InnerSection>
              <Span10>(c)</Span10>
              where a fee or cost is referred to in the Advertisement, it gives a realistic impression of the overall level of fees and costs a consumer is likely to pay, including any indirect fees or costs;
            </InnerSection>
            <InnerSection>
              <Span10>(d)</Span10>
              where the Advertisement compares different products, comparisons are made between products that have sufficiently similar features;

            </InnerSection>
            <InnerSection>
              <Span10>(e)</Span10>
              past performance information will be accompanied by a warning that past performance is not indicative of future performance;

            </InnerSection>
            <InnerSection>
              <Span10>(f)</Span10>
              forecasts about the future performance of a financial product are based on reasonable assumptions and will state that the forecasts are not guaranteed to occur;

            </InnerSection>
            <InnerSection>
              <Span10>(g)</Span10>
              where the Advertisement states or implies that a particular benefit is likely, it includes a statement about the risks associated with obtaining that benefit, or about the product generally;

            </InnerSection>
            <InnerSection>
              <Span10>(h)</Span10>
              the Advertisement includes a clear explanation of the assumptions made in predicting the benefit;

            </InnerSection>
            <InnerSection>
              <Span10>(i)</Span10>
              claims about features of a product or service are made because a consumer can reasonably expect those features to be available to them;

            </InnerSection>
            <InnerSection>
              <Span10>(j)</Span10>
              the Advertisement does not overstate the potential benefits or create unrealistic expectations by giving undue prominence to the benefits compared with the risks;

            </InnerSection>
            <InnerSection>
              <Span10>(k)</Span10>
              the effect that fees and costs may have on returns and benefits over time are clearly stated, and not hidden or difficult to understand;

            </InnerSection>
            <InnerSection>
              <Span10>(l)</Span10>
              information about the risks of a product are clear, and not hidden or difficult to understand, and are given sufficient prominence to information about returns and benefits;

            </InnerSection>
            <InnerSection>
              <Span10>(m)</Span10>
              <div style={{ flexDirection: 'column' }}>
                the Advertisement:
                <div>
                  <InnerSection>
                    <Span10>(i)</Span10>
                    is not false or misleading;
                  </InnerSection>
                  <InnerSection>
                    <Span10>(ii)</Span10>
                    does not contain a statement that is misleading, false or deceptive;
                  </InnerSection>
                  <InnerSection>
                    <Span10>(iii)</Span10>
                    does not dishonestly conceal facts;
                  </InnerSection>
                  <InnerSection>
                    <Span10>(iv)</Span10>
                    does not make representations about future matters without reasonable grounds;
                  </InnerSection>
                  <InnerSection>
                    <Span10>(v)</Span10>
                    does not contain terms in relation to financial services that are misleading or
                    deceptive or are likely to mislead or deceive;
                  </InnerSection>
                  <InnerSection>
                    <Span10>(vi)</Span10>
                    does not make false representations that are misleading in relation to financial products that involve interests in land; and
                  </InnerSection>
                  <InnerSection>
                    <Span10>(vii)</Span10>
                    does not engage in bait advertising.
                  </InnerSection>
                </div>
              </div>
            </InnerSection>
          </div>
        </span>
      </Section>
    </Container>
  );
}
